/* Tradeline site chrome: announcement bar, countdown, sticky header, mega menu,
 * "All categories" dropdown, predictive search, menu drawer, localization, footer accordions.
 * Plain JS custom elements; depends only on window.Tradeline from tl-theme.js (optional). */
(() => {
  if (window.__tlHeaderLoaded) return;
  window.__tlHeaderLoaded = true;

  const T = window.Tradeline || {};
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;
  const canHover = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const focusables = (root) => Array.from(root.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, summary, [tabindex]:not([tabindex="-1"])'))
    .filter((el) => el.offsetParent !== null || el.getClientRects().length);

  /* ---------- Announcement bar ---------- */
  class TlAnnouncement extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-announce-slide]'));
      this.index = Math.max(0, this.slides.findIndex((s) => !s.hidden));
      this.paused = false;
      this.userPaused = false;
      if (this.slides.length < 2) return;
      this.querySelector('[data-announce-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('[data-announce-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
      this.pauseBtn = this.querySelector('[data-announce-pause]');
      this.pauseBtn?.addEventListener('click', () => this.togglePause());
      ['mouseenter', 'focusin'].forEach((ev) => this.addEventListener(ev, () => { this.paused = true; this.stop(); }));
      ['mouseleave', 'focusout'].forEach((ev) => this.addEventListener(ev, (e) => {
        if (ev === 'focusout' && this.contains(e.relatedTarget)) return;
        this.paused = false;
        this.start();
      }));
      if (reduceMotion() && this.pauseBtn) this.pauseBtn.hidden = true;
      // Rotation waits for the shopper's first interaction and pauses off screen, so the
      // bar holds still while the page loads.
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(([entry]) => { this.offscreen = !entry.isIntersecting; this.start(); });
        this.io.observe(this);
      }
      const events = ['pointerdown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
      const go = () => {
        events.forEach((t) => window.removeEventListener(t, go, { capture: true }));
        this.engaged = true;
        this.start();
      };
      this.disarm = () => events.forEach((t) => window.removeEventListener(t, go, { capture: true }));
      events.forEach((t) => window.addEventListener(t, go, { capture: true, passive: true }));
    }
    disconnectedCallback() { this.stop(); if (this.io) this.io.disconnect(); if (this.disarm) this.disarm(); }
    go(i, fromUser) {
      const n = this.slides.length;
      this.index = (i + n) % n;
      this.slides.forEach((s, k) => { s.hidden = k !== this.index; });
      // Announce manual changes only; automatic rotation stays silent.
      const track = this.querySelector('.tl-announce__track');
      if (track) track.setAttribute('aria-live', fromUser ? 'polite' : 'off');
      if (fromUser) this.start();
    }
    start() {
      this.stop();
      if (this.dataset.autoplay !== 'true' || this.paused || this.userPaused || reduceMotion()) return;
      if (!this.engaged || this.offscreen) return;
      if (window.Shopify && Shopify.designMode) return;
      const ms = Math.max(3, Number(this.dataset.speed || 5)) * 1000;
      this.timer = setInterval(() => this.go(this.index + 1, false), ms);
    }
    stop() { clearInterval(this.timer); }
    togglePause() {
      this.userPaused = !this.userPaused;
      const b = this.pauseBtn;
      b.setAttribute('aria-pressed', String(this.userPaused));
      b.setAttribute('aria-label', this.userPaused ? (b.dataset.labelPlay || b.getAttribute('aria-label')) : (b.dataset.labelPause || b.getAttribute('aria-label')));
      b.querySelector('[data-icon-pause]')?.toggleAttribute('hidden', this.userPaused);
      b.querySelector('[data-icon-play]')?.toggleAttribute('hidden', !this.userPaused);
      if (this.userPaused) this.stop(); else this.start();
    }
    // Theme editor: show the selected block.
    select(blockEl) { const i = this.slides.indexOf(blockEl); if (i > -1) { this.go(i, false); this.stop(); } }
  }
  if (!customElements.get('tl-announcement')) customElements.define('tl-announcement', TlAnnouncement);

  /* ---------- Countdown ---------- */
  class TlCountdown extends HTMLElement {
    connectedCallback() {
      this.end = Number(this.dataset.end || 0) * 1000;
      this.parts = {};
      this.querySelectorAll('[data-cd]').forEach((el) => { this.parts[el.dataset.cd] = el; });
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      const left = Math.floor((this.end - Date.now()) / 1000);
      if (!this.end || left <= 0) {
        clearInterval(this.timer);
        const slide = this.closest('[data-announce-slide]');
        if (slide) {
          slide.remove();
          const bar = this.closest('tl-announcement');
          if (bar) {
            bar.slides = (bar.slides || []).filter((s) => s !== slide);
            if (!bar.slides.length) bar.closest('.tl-announce')?.setAttribute('hidden', '');
            else bar.go(0, false);
          }
        } else this.hidden = true;
        return;
      }
      const pad = (n) => String(n).padStart(2, '0');
      const v = { d: Math.floor(left / 86400), h: Math.floor((left % 86400) / 3600), m: Math.floor((left % 3600) / 60), s: left % 60 };
      Object.entries(v).forEach(([k, n]) => { if (this.parts[k]) this.parts[k].textContent = pad(n); });
      if (this.parts.d) this.parts.d.parentElement.hidden = v.d === 0;
    }
  }
  if (!customElements.get('tl-announce-countdown')) customElements.define('tl-announce-countdown', TlCountdown);

  /* ---------- Header: sticky modes + height var ---------- */
  class TlHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || this;
      this.mode = this.dataset.sticky;
      this.setHeight();
      this.ro = 'ResizeObserver' in window ? new ResizeObserver(() => this.setHeight()) : null;
      this.ro?.observe(this);
      if (this.mode === 'scroll_up') {
        this.lastY = window.scrollY;
        this.onScroll = () => {
          const y = window.scrollY;
          const h = this.offsetHeight;
          const menuOpen = this.querySelector('[aria-expanded="true"][data-menu-toggle]');
          if (y > this.lastY && y > h * 1.5 && !menuOpen && !this.contains(document.activeElement)) {
            this.section.classList.add('tl-header-hidden');
          } else if (y < this.lastY) {
            this.section.classList.remove('tl-header-hidden');
          }
          this.section.classList.toggle('tl-header-scrolled', y > 4);
          this.lastY = Math.max(0, y);
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
      } else if (this.mode === 'always') {
        this.onScroll = () => this.section.classList.toggle('tl-header-scrolled', window.scrollY > 4);
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }
      this.addEventListener('focusin', () => this.section.classList.remove('tl-header-hidden'));
    }
    disconnectedCallback() {
      this.ro?.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }
    setHeight() {
      document.documentElement.style.setProperty('--tl-header-height', this.offsetHeight + 'px');
      this.section.style.setProperty('--tl-header-h', this.offsetHeight + 'px');
    }
  }
  if (!customElements.get('tl-header')) customElements.define('tl-header', TlHeader);

  /* ---------- Mega menu + departments dropdown ---------- */
  class TlMegaNav extends HTMLElement {
    connectedCallback() {
      this.toggles = Array.from(this.querySelectorAll('[data-menu-toggle]'));
      this.tops = Array.from(this.querySelectorAll('.tl-nav__list > li > .tl-nav__link'));
      this.timers = new WeakMap();

      this.toggles.forEach((btn) => {
        btn.addEventListener('click', () => this.toggle(btn));
        btn.addEventListener('keydown', (e) => this.onToggleKey(e, btn));
        const item = btn.parentElement;
        item.addEventListener('mouseenter', () => { if (canHover() && !btn.hasAttribute('data-dept-toggle')) this.intent(btn, true); });
        item.addEventListener('mouseleave', () => { if (canHover() && !btn.hasAttribute('data-dept-toggle')) this.intent(btn, false); });
      });
      this.tops.forEach((el) => el.addEventListener('keydown', (e) => this.onTopKey(e, el)));

      // Panels: arrow keys move between links, Escape closes.
      this.querySelectorAll('[data-menu-panel]').forEach((panel) => {
        panel.addEventListener('keydown', (e) => this.onPanelKey(e, panel));
      });

      // Department sub-panels: switch on hover/focus/click.
      this.deptItems = Array.from(this.querySelectorAll('[data-dept-item]'));
      this.deptItems.forEach((b) => {
        b.addEventListener('click', () => this.showDept(b));
        b.addEventListener('mouseenter', () => { if (canHover()) this.showDept(b); });
        b.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'Enter' || e.key === ' ') {
            if (e.key !== 'ArrowRight') return; // click handles Enter/Space
            e.preventDefault();
            this.showDept(b);
            const sub = document.getElementById(b.getAttribute('aria-controls'));
            sub && focusables(sub)[0]?.focus();
          }
        });
      });

      this.onDocClick = (e) => { if (!this.contains(e.target)) this.closeAll(); };
      this.onDocKey = (e) => { if (e.key === 'Escape') { const open = this.openToggle(); if (open) { this.close(open); open.focus(); } } };
      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onDocKey);
      this.addEventListener('focusout', (e) => {
        if (!e.relatedTarget) return;
        this.toggles.forEach((btn) => {
          if (btn.getAttribute('aria-expanded') === 'true' && !btn.parentElement.contains(e.relatedTarget)) this.close(btn);
        });
      });
    }
    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onDocKey);
    }
    panelOf(btn) { return document.getElementById(btn.getAttribute('aria-controls')); }
    openToggle() { return this.toggles.find((b) => b.getAttribute('aria-expanded') === 'true'); }
    open(btn) {
      this.toggles.forEach((b) => { if (b !== btn) this.close(b); });
      const p = this.panelOf(btn);
      if (!p) return;
      btn.setAttribute('aria-expanded', 'true');
      p.hidden = false;
      requestAnimationFrame(() => p.classList.add('is-open'));
      this.classList.add('has-open');
    }
    close(btn) {
      const p = this.panelOf(btn);
      btn.setAttribute('aria-expanded', 'false');
      if (p) { p.classList.remove('is-open'); p.hidden = true; }
      if (!this.openToggle()) this.classList.remove('has-open');
    }
    closeAll() { this.toggles.forEach((b) => this.close(b)); }
    toggle(btn) { if (btn.getAttribute('aria-expanded') === 'true') this.close(btn); else this.open(btn); }
    intent(btn, opening) {
      clearTimeout(this.timers.get(btn));
      const delay = opening ? (this.openToggle() ? 0 : 140) : 260;
      this.timers.set(btn, setTimeout(() => (opening ? this.open(btn) : this.close(btn)), delay));
    }
    onToggleKey(e, btn) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.open(btn);
        const p = this.panelOf(btn);
        p && focusables(p)[0]?.focus();
      }
    }
    onTopKey(e, el) {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') return;
      e.preventDefault();
      const i = this.tops.indexOf(el);
      const n = this.tops.length;
      let next = i;
      if (e.key === 'ArrowRight') next = (i + 1) % n;
      else if (e.key === 'ArrowLeft') next = (i - 1 + n) % n;
      else if (e.key === 'Home') next = 0;
      else next = n - 1;
      const wasOpen = !!this.openToggle();
      this.tops[next].focus();
      if (wasOpen && this.tops[next].hasAttribute('data-menu-toggle')) this.open(this.tops[next]);
      else if (wasOpen) this.closeAll();
    }
    onPanelKey(e, panel) {
      const sub = e.target.closest('.tl-dept__sub');
      if (e.key === 'ArrowLeft' && sub) {
        e.preventDefault();
        this.querySelector(`[aria-controls="${sub.id}"]`)?.focus();
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      // Inside the departments list, Up/Down move between departments only.
      const scope = e.target.closest('.tl-dept__list > li > [data-dept-item], .tl-dept__list > li > .tl-dept__link')
        ? Array.from(panel.querySelectorAll('.tl-dept__list > li > .tl-dept__link'))
        : focusables(e.target.closest('.tl-dept__sub') || panel);
      const i = scope.indexOf(document.activeElement);
      if (i === -1) return;
      e.preventDefault();
      const next = e.key === 'ArrowDown' ? Math.min(scope.length - 1, i + 1) : Math.max(0, i - 1);
      scope[next].focus();
      if (scope[next].hasAttribute('data-dept-item')) this.showDept(scope[next]);
    }
    showDept(b) {
      this.deptItems.forEach((x) => {
        const on = x === b;
        x.setAttribute('aria-expanded', String(on));
        const sub = document.getElementById(x.getAttribute('aria-controls'));
        if (sub) sub.hidden = !on;
      });
    }
  }
  if (!customElements.get('tl-mega-nav')) customElements.define('tl-mega-nav', TlMegaNav);

  /* ---------- Predictive search ---------- */
  class TlPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.form = this.querySelector('form');
      this.panel = this.querySelector('[data-predictive-panel]');
      this.status = this.querySelector('[data-predictive-status]');
      this.clearBtn = this.querySelector('[data-search-clear]');
      this.cache = new Map();
      if (!this.input) return;
      const syncClear = () => { if (this.clearBtn) this.clearBtn.hidden = !this.input.value; };
      syncClear();
      this.clearBtn?.addEventListener('click', () => { this.input.value = ''; syncClear(); this.close(); this.input.focus(); });
      if (this.dataset.enabled !== 'true' || !this.panel) { this.input.addEventListener('input', syncClear); return; }

      this.search = (T.debounce || ((fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; }))(() => this.fetchResults(), 220);
      this.input.addEventListener('input', () => {
        syncClear();
        const q = this.input.value.trim();
        if (!q) { this.close(); return; }
        this.search();
      });
      this.input.addEventListener('focus', () => { if (this.input.value.trim() && this.panel.innerHTML.trim()) this.openPanel(); });
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) setTimeout(() => { if (!this.contains(document.activeElement)) this.close(); }, 0); });
      this.panel.addEventListener('mousedown', (e) => { if (e.target.closest('[role="option"]')) e.preventDefault(); });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(); });
    }
    async fetchResults() {
      const q = this.input.value.trim();
      if (!q) return;
      if (this.cache.has(q)) { this.render(this.cache.get(q)); return; }
      this.abort?.abort();
      this.abort = new AbortController();
      this.setAttribute('aria-busy', 'true');
      const params = new URLSearchParams({
        q,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,query,article,page',
        'resources[limit]': '6',
        'resources[options][fields]': 'title,product_type,variants.sku,variants.title,vendor,tag'
      });
      try {
        const res = await fetch(`${this.dataset.url}?${params.toString()}`, { signal: this.abort.signal });
        if (!res.ok) throw new Error(String(res.status));
        const text = await res.text();
        const results = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-predictive-results]');
        const html = results ? results.innerHTML : '';
        this.cache.set(q, html);
        if (this.input.value.trim() === q) this.render(html);
      } catch (err) {
        if (err.name !== 'AbortError') this.close();
      } finally {
        this.removeAttribute('aria-busy');
      }
    }
    render(html) {
      this.panel.innerHTML = html;
      this.active = -1;
      this.input.removeAttribute('aria-activedescendant');
      if (!html.trim()) { this.close(); return; }
      this.openPanel();
      const count = this.panel.querySelector('[data-predictive-count]');
      if (this.status && count) this.status.textContent = count.textContent.trim();
    }
    options() { return Array.from(this.panel.querySelectorAll('[role="option"]')); }
    openPanel() { this.panel.hidden = false; this.input.setAttribute('aria-expanded', 'true'); this.classList.add('is-open'); }
    close() {
      if (!this.panel) return;
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.classList.remove('is-open');
      this.active = -1;
    }
    setActive(i) {
      const opts = this.options();
      if (!opts.length) return;
      this.active = (i + opts.length) % opts.length;
      opts.forEach((o, k) => o.setAttribute('aria-selected', String(k === this.active)));
      const el = opts[this.active];
      this.input.setAttribute('aria-activedescendant', el.id);
      el.scrollIntoView({ block: 'nearest' });
    }
    onKey(e) {
      const open = !this.panel.hidden;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (!open && this.input.value.trim()) { this.panel.innerHTML.trim() ? this.openPanel() : this.fetchResults(); return; }
          this.setActive((this.active ?? -1) + 1);
          break;
        case 'ArrowUp':
          if (!open) return;
          e.preventDefault();
          this.setActive((this.active ?? 0) - 1);
          break;
        case 'Escape':
          if (open) { e.preventDefault(); e.stopPropagation(); this.close(); }
          else if (this.input.value) { this.input.value = ''; if (this.clearBtn) this.clearBtn.hidden = true; }
          break;
        case 'Enter': {
          const opt = open && this.active > -1 ? this.options()[this.active] : null;
          if (opt) { e.preventDefault(); window.location.href = opt.href; }
          break;
        }
        default:
      }
    }
  }
  if (!customElements.get('tl-predictive-search')) customElements.define('tl-predictive-search', TlPredictiveSearch);

  /* ---------- Drawers: aria-expanded sync + focus trap (menu + cart) ---------- */
  const syncExpanded = (drawer) => {
    const open = drawer.classList.contains('is-open');
    document.querySelectorAll(`[aria-controls="${drawer.id}"]`).forEach((b) => b.setAttribute('aria-expanded', String(open)));
  };
  const watchDrawer = (drawer) => {
    if (!drawer || drawer._tlWatched) return;
    drawer._tlWatched = true;
    new MutationObserver(() => syncExpanded(drawer)).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  };
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const drawer = document.querySelector('#MenuDrawer.is-open');
    if (!drawer) return;
    const items = focusables(drawer.querySelector('.tl-drawer__panel') || drawer);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });
  // Close the menu drawer when switching to desktop.
  window.matchMedia('(min-width: 990px)').addEventListener?.('change', (mq) => {
    if (mq.matches) { const d = document.querySelector('#MenuDrawer.is-open'); if (d && T.closeDrawer) T.closeDrawer(d); }
  });

  /* ---------- Localization: submit on change ---------- */
  document.addEventListener('change', (e) => {
    const sel = e.target.closest('form[data-tl-localization] select');
    if (sel) sel.form.submit();
  });

  /* ---------- Header compare icon ---------- */
  const syncCompare = () => {
    const n = T.compare ? T.compare.items().length : 0;
    document.querySelectorAll('[data-header-compare-count]').forEach((el) => { el.textContent = n; el.hidden = n === 0; });
  };
  document.addEventListener('tl:compare:changed', syncCompare);
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-header-compare]');
    if (!btn) return;
    const opener = document.querySelector('[data-compare-open]');
    if (T.compare && T.compare.items().length && opener) opener.click();
    else if (T.toast) T.toast(btn.dataset.emptyMessage || '');
  });

  /* ---------- Footer accordions: collapsible on mobile only ---------- */
  const footerAccordions = () => {
    const mobile = !window.matchMedia('(min-width: 750px)').matches;
    document.querySelectorAll('.tl-footer__acc').forEach((d) => { d.open = !mobile; });
  };
  document.addEventListener('click', (e) => {
    const summary = e.target.closest('.tl-footer__acc > summary');
    if (summary && window.matchMedia('(min-width: 750px)').matches) e.preventDefault();
  });
  window.matchMedia('(min-width: 750px)').addEventListener?.('change', footerAccordions);

  /* ---------- Multi-currency: refresh free-shipping messages with live cart totals ---------- */
  const refreshFsb = () => {
    const rate = window.Shopify && Shopify.currency ? Number(Shopify.currency.rate || 1) : 1;
    if (rate !== 1 && document.querySelector('[data-fsb]') && T.cart && T.updateFreeShipping) {
      T.cart.get().then((cart) => T.updateFreeShipping(cart)).catch(() => {});
    }
  };

  const init = () => {
    watchDrawer(document.getElementById('MenuDrawer'));
    watchDrawer(document.getElementById('CartDrawer'));
    syncCompare();
    footerAccordions();
    refreshFsb();
    const ok = document.querySelector('[data-newsletter-success]');
    if (ok) ok.focus();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();

  /* ---------- Theme editor ---------- */
  document.addEventListener('shopify:section:load', () => { init(); });
  document.addEventListener('shopify:block:select', (e) => {
    const bar = e.target.closest('tl-announcement');
    if (bar && bar.select) bar.select(e.target);
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    const bar = e.target.closest('tl-announcement');
    if (bar && bar.start) bar.start();
  });
})();
